import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ExerciseData, SpeechSynthesisHookReturn } from "../types";

// Singleton AudioContext to prevent browser limit errors
let sharedAudioContext: AudioContext | null = null;

export const useSpeechSynthesis = (
  exerciseData: ExerciseData | null,
  selectedLanguageCode: string,
  ttsSpeed: number = 1.0
): SpeechSynthesisHookReturn => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasUnlockedRef = useRef(false);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const audioCache = useRef<Record<string, ArrayBuffer>>({});

  const unlockAudio = useCallback(() => {
    if (typeof window === "undefined") return;

    // Initialize singleton if needed
    if (!sharedAudioContext) {
      const AudioContextCtor =
        window.AudioContext ||
        (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

      if (AudioContextCtor) {
        sharedAudioContext = new AudioContextCtor();
      }
    }

    if (!sharedAudioContext) return;

    if (sharedAudioContext.state === "suspended") {
      sharedAudioContext.resume().catch(() => undefined);
    }

    // Only run the unlock buffer routine once per session/context
    if (hasUnlockedRef.current) return;

    try {
      const ctx = sharedAudioContext;
      console.log("[TTS] Unlocking audio context, state:", ctx.state);

      const buffer = ctx.createBuffer(1, 1, 22050);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);

      if (ctx.state === "suspended") {
        ctx.resume().then(() => console.log("[TTS] Check: Audio context resumed"));
      }
    } catch (e) {
      console.warn("[TTS] Unlock WebAudio failed", e);
    }

    try {
      // Also try to unlock HTML Audio element
      const silentAudio = new Audio(
        "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgLsAAAB3AQACABAAZGF0YQAAAAA="
      );
      silentAudio.volume = 0;
      const playPromise = silentAudio.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => {
            console.log("[TTS] Silent HTML Audio unlocked");
            hasUnlockedRef.current = true;
          })
          .catch((e) => console.log("[TTS] Silent HTML Audio unlock prevented", e));
      } else {
        hasUnlockedRef.current = true;
      }
    } catch {
      // Ignore unlock failures; playback will handle errors.
    }
  }, []);

  const stopSpeech = useCallback((reason: string = "explicit") => {
    console.log(`[TTS] stopSpeech called. Reason: ${reason}`);
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      if (audioRef.current.src) {
        URL.revokeObjectURL(audioRef.current.src);
      }
      audioRef.current = null;
    }

    if (sourceRef.current) {
      try {
        sourceRef.current.stop();
      } catch {
        // ignore stop errors
      }
      try {
        sourceRef.current.disconnect();
      } catch {
        // ignore disconnect errors
      }
      sourceRef.current = null;
    }

    setIsSpeaking(false);
  }, []);

  const playAudioBuffer = useCallback(async (buffer: ArrayBuffer) => {
    const context = sharedAudioContext;

    // Try WebAudio first
    if (context) {
      try {
        if (context.state === "suspended") {
          await context.resume();
        }

        const decodedBuffer = await context.decodeAudioData(buffer.slice(0));

        const source = context.createBufferSource();
        source.buffer = decodedBuffer;
        source.connect(context.destination);
        sourceRef.current = source;

        return new Promise<void>((resolve) => {
          source.onended = () => {
            if (sourceRef.current === source) {
              sourceRef.current.disconnect();
              sourceRef.current = null;
            }
            resolve();
          };
          source.start(0);
        });
      } catch (decodeError) {
        // Fallback to HTMLAudio
      }
    }

    // Fallback to HTMLAudioElement
    const audioBlob = new Blob([buffer], { type: "audio/mpeg" }); // Force correct mime
    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);
    audio.preload = "auto";
    audio.muted = false;
    audio.volume = 1;
    audioRef.current = audio;

    return new Promise<void>((resolve) => {
      const handleEnded = () => {
        if (audioRef.current?.src) {
          URL.revokeObjectURL(audioRef.current.src);
        }
        audioRef.current = null;
        resolve();
      };

      const handleError = (e: Event) => {
        console.error("HTMLAudio playback error:", e);
        if (audioRef.current?.src) {
          URL.revokeObjectURL(audioRef.current.src);
        }
        audioRef.current = null;
        resolve();
      };

      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("error", handleError);

      const playPromise = audio.play();
      if (playPromise) {
        playPromise.catch(() => resolve());
      }
    });
  }, []);

  const speakSentence = useCallback(async () => {
    if (!exerciseData?.content) {
      return;
    }

    unlockAudio();
    stopSpeech("start_new_sentence");

    const fullSentence = exerciseData.content.replace(
      /____+(\s*\([^)]+\))?/,
      exerciseData.correctAnswer
    );

    const controller = new AbortController();
    abortRef.current = controller;
    setIsSpeaking(true);

    const speed = Math.max(0.5, Math.min(1.5, ttsSpeed));
    const cacheKey = `${fullSentence}|${selectedLanguageCode}|${speed}`;

    if (audioCache.current[cacheKey]) {
      console.log(
        `[TTS] Using cached audio for: "${fullSentence.substring(0, 20)}..." at ${speed}x`
      );
      await playAudioBuffer(audioCache.current[cacheKey]);
      setIsSpeaking(false);
      return;
    }

    try {
      const response = await fetch("/api/tts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          text: fullSentence,
          speaker: "Guide",
          languageCode: selectedLanguageCode,
          speed,
          enableChunking: false,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`TTS request failed: ${response.status}`);
      }

      const audioBuffer = await response.arrayBuffer();
      audioCache.current[cacheKey] = audioBuffer;
      await playAudioBuffer(audioBuffer);
    } catch (error) {
      if ((error as Error).name === "AbortError") {
        // Intentionally aborted
      } else {
        console.error("Failure in speakSentence:", error);
      }
    } finally {
      setIsSpeaking(false);
    }
  }, [
    exerciseData?.content,
    exerciseData?.correctAnswer,
    playAudioBuffer,
    selectedLanguageCode,
    stopSpeech,
    ttsSpeed,
    unlockAudio,
  ]);

  const toggleSpeech = useCallback(() => {
    if (isSpeaking) {
      stopSpeech("user_toggle_off");
    } else {
      unlockAudio();
      speakSentence();
    }
  }, [isSpeaking, speakSentence, stopSpeech, unlockAudio]);

  useEffect(() => {
    return () => {
      stopSpeech("hook_unmount_cleanup");
    };
  }, [stopSpeech]);

  return useMemo(
    () => ({
      isSpeaking,
      speakSentence,
      toggleSpeech,
      stopSpeech,
      unlockAudio,
    }),
    [isSpeaking, speakSentence, toggleSpeech, stopSpeech, unlockAudio]
  );
};
